import { FC, useContext, useEffect, useCallback } from "react";
import { AxiosError } from "axios";
import { GlobalModalContext } from "src/utils/GlobalModal/GlobalModalContext";
import useCreatePosition from "src/Pages/Company/OrganizationStructure/api/useCreatePosition";
import useChangePosition from "src/Pages/Company/OrganizationStructure/api/useChangePosition";
import __ from "src/utils/Translations";
import { Controller, useForm } from "react-hook-form";
import { TextField } from "@mui/material";
import { getErrorMessages } from "src/Pages/Company/OrganizationStructure/utils/errors/errorMessages";
import { handleBackendErrors } from "src/Pages/Company/OrganizationStructure/utils/errors/handleBackendErrors";
import { standardSchemaResolver } from "@hookform/resolvers/standard-schema";
import { positionSchema } from "src/Pages/Company/OrganizationStructure/Positions/Modal/Position.schema";
import { useLogStatus } from "src/Pages/Company/OrganizationStructure/utils/NewRelicReporter/useLogStatus/useLogStatus";
import { NewRelicLogLevel } from "src/Pages/Company/OrganizationStructure/utils/NewRelicReporter/useLogStatus/useLogStatus.models";

interface PositionFormData {
  positionName: string;
}

interface PositionModalProps {
  companyId: string;
  structureId: string;
  mode: "create" | "edit";
  data?: {
    id: string;
    value: string;
  };
  onClose?: () => void;
}

const PositionModal: FC<PositionModalProps> = ({
  companyId,
  structureId,
  mode,
  data,
  onClose,
}) => {
  const { setModal, resetModal } = useContext(GlobalModalContext);
  const isEditMode = mode === "edit";

  const logStatus = useLogStatus();

  const {
    control,
    handleSubmit,
    reset,
    setError,
    watch,
    formState: { isValid },
  } = useForm<PositionFormData>({
    defaultValues: {
      positionName: data?.value || "",
    },
    mode: "onChange",
    resolver: standardSchemaResolver(positionSchema),
  });

  const positionName = watch("positionName");
  const isSaveDisabled = !positionName?.trim() || !isValid;

  const { mutate: createPosition, isPending: isCreating } = useCreatePosition(
    companyId,
    structureId
  );
  const { mutate: editPosition, isPending: isEditing } = useChangePosition(
    companyId,
    structureId
  );

  const isPending = isEditMode ? isEditing : isCreating;

  const handleServerError = useCallback(
    (error: any) => {
      logStatus({
        type: NewRelicLogLevel.ERROR,
        message: `PositionModal: ${
          isEditMode ? "editPosition" : "createPosition"
        } failed`,
        statusDetails: {
          source: "API",
          operation: isEditMode ? "editPosition" : "createPosition",
          status: (error as AxiosError)?.response?.status,
          url: (error as AxiosError)?.config?.url,
        },
        errorObject: error instanceof Error ? error : new Error(String(error)),
      });

      const backendErrors = error?.response?.data?.errors;

      const backendFieldToFormField: Record<string, keyof PositionFormData> = {
        value: "positionName",
      };

      if (backendErrors && Array.isArray(backendErrors)) {
        const processed = handleBackendErrors<PositionFormData>({
          errors: backendErrors,
          errorMessages: getErrorMessages(),
          context: "POSITION",
        });

        processed.forEach(({ field, message }) => {
          const fieldName: keyof PositionFormData =
            backendFieldToFormField[field] || "positionName";

          setError(fieldName, {
            type: "server",
            message,
          });
        });
      } else {
        setError("positionName", {
          type: "server",
          message: __("Wystąpił błąd"),
        });
      }
    },
    [setError]
  );

  const handleSave = useCallback(
    (formValues: PositionFormData) => {
      const value = formValues.positionName.trim();
      if (!value) return;

      const onSuccess = () => {
        resetModal();
        reset();
        if (onClose) onClose();
      };

      isEditMode
        ? editPosition(
            {
              positionId: data?.id || "",
              formData: { value },
            },
            {
              onSuccess,
              onError: handleServerError,
            }
          )
        : createPosition(
            { value },
            {
              onSuccess,
              onError: handleServerError,
            }
          );
    },
    [
      isEditMode,
      createPosition,
      editPosition,
      data?.id,
      resetModal,
      reset,
      onClose,
      handleServerError,
    ]
  );

  const onCancel = useCallback(() => {
    resetModal();
    reset();
    if (onClose) onClose();
  }, [resetModal, reset, onClose]);

  const handleSaveClick = useCallback(
    () => handleSubmit(handleSave)(),
    [handleSubmit, handleSave]
  );

  const customElements = (
    <Controller
      name="positionName"
      control={control}
      render={({ field, fieldState }) => (
        <TextField
          {...field}
          label={__("Nazwa stanowiska")}
          fullWidth
          sx={{ mt: 2, mb: 1 }}
          error={!!fieldState.error}
          helperText={fieldState.error?.message}
          slotProps={{
            htmlInput: {
              "data-testid": "os__positions-modal-name-input",
            },
          }}
        />
      )}
    />
  );

  useEffect(() => {
    setModal({
      open: true,
      title: isEditMode ? __("Edytuj stanowisko") : __("Nowe stanowisko"),
      maxWidth: 628,
      disableExitButton: true,
      disableOutsideClick: true,
      customElements,
      actions: [
        {
          label: __("Anuluj"),
          variant: "outlined" as const,
          callbackFn: onCancel,
        },
        {
          label: __("Zapisz"),
          variant: "contained" as const,
          callbackFn: handleSaveClick,
          loading: isPending,
          disabled: isSaveDisabled,
        },
      ],
    });
  }, [
    setModal,
    customElements,
    onCancel,
    handleSaveClick,
    isPending,
    isSaveDisabled,
    isEditMode,
  ]);

  // eslint-disable-next-line arrow-body-style
  useEffect(() => {
    return () => {
      resetModal();
    };
  }, [resetModal]);

  return null;
};

export default PositionModal;
